import { createApi } from "@reduxjs/toolkit/query/react";
import { isObject } from "lodash";
import { produce } from "immer";
import { axiosBaseQuery } from "./axiosBaseQuery";
import { DynamicPage } from "../objects/dynamicPage";
import { PageBuilderMenu, TPageBuilderMenu } from "../objects/pageBuilderMenu";
import { MenuContents, TMenuContents } from "../blocks/menu";
import { detectEnv } from "../../../../Common/utils/detectEnv";
import { FunnelComposition } from "../objects/funnelComposition";
import { Destination } from "../../../../Itinerary/objects/destination";
import { SchemaTemplate } from "../objects/schemaTemplate";
import { TripType } from "../../../../pages/objects/tripType";
import { User } from "../../../MaterialTripList/objects/user";
import { Styles } from "../objects/styles";

type AwsFile = {
    id: number,
    bucket_name: string,
    file_name: string,
    object_name: string,
    type: string,
    file_category: {
        id: number,
        name: string | null,
        icon: string
    } | null
}

export const pageBuilderApi = createApi({
    reducerPath: 'api.pageBuilder',
    baseQuery: axiosBaseQuery({
        baseUrl: (queryParameters) => {
            const config: {
                cms_list?: {
                    id: number,
                    is_active: boolean,
                    name: string,
                    domain: string,
                    authentication_required: boolean
                }[]
            } = JSON.parse(localStorage.getItem('config') ?? '{}');
            const cms = config.cms_list?.find((item) => {
                const domainName = window.location.hostname.startsWith("www.") ?
                    window.location.hostname.replace("www.", "") :
                    window.location.hostname;
                return item.domain === domainName;
            }) ?? null;
            if (!cms) {
                throw new Error('Could not find cms config.');
            }

            if (
                queryParameters.data instanceof FormData
            ) {
                if (!queryParameters.headers) {
                    queryParameters.headers = {};
                }
                queryParameters.headers["Content-Type"] = "multipart/form-data";
            }

            if (isObject(queryParameters) && queryParameters.noCms) {
                return `${API_HREF}client/${window.id_owner}`;
            }
            return `${API_HREF}client/${window.id_owner}/cms/${cms.id}`;
        }
    }),
    tagTypes: [
        'dynamic-pages',
        'dynamic-page',
        'menus',
        'menu',
        'funnel-composition',
        'destinations',
        'home-page-destinations',
        'schema-templates',
        'trip-types',
        'icons',
        'user'
    ],
    endpoints: (builder) => ({
        createDynamicPage: builder.mutation<
            DynamicPage,
            Partial<Omit<DynamicPage, 'id' | 'parent'> & { parent: number | null }>
        >({
            query: (page) => {
                return {
                    method: 'POST',
                    url: `/dynamic-page/`,
                    data: page
                }
            },
            invalidatesTags: () => [
                'dynamic-pages',
                'dynamic-page'
            ]
        }),
        createSchemaTemplate: builder.mutation<
            DynamicPage,
            Partial<Omit<SchemaTemplate, 'id'>>
        >({
            query: (data) => {
                return {
                    method: 'POST',
                    url: '/schema-template/',
                    data: data
                }
            },
            invalidatesTags: ['schema-templates']
        }),
        createMenu: builder.mutation<
            PageBuilderMenu,
            Omit<PageBuilderMenu, 'id' | 'creator' | 'last_author' | 'modified_date' | 'pages'> & {
                pages: number[]
            }
        >({
            query: (menu) => {
                const config: {
                    cms_list?: {
                        id: number,
                        is_active: boolean,
                        name: string,
                        domain: string,
                        authentication_required: boolean
                    }[]
                } = JSON.parse(localStorage.getItem('config') ?? '{}');
                const cms = config.cms_list?.find((item) => {
                    return item.domain.toLowerCase() === window.location.host.toLowerCase();
                }) ?? null;
                return {
                    method: 'POST',
                    url: `/menu/`,
                    data: {
                        ...menu,
                        cms: cms?.id
                    }
                }
            },
            invalidatesTags: () => [
                'menus',
                'menu'
            ]
        }),
        uploadFile: builder.mutation<
            AwsFile,
            File
        >({
            query: (file) => {
                const data = new FormData();
                data.append("file_object", file);
                data.append("type", 'svg');
                if (detectEnv().env === 'production') {
                    data.append("prefix", 'icon-prod');
                } else {
                    data.append("prefix", 'icon-preprod');
                }
                data.append('bucket_name', 'facilitatrip-staging-picture');
                data.append("file_name", file.name);
                return {
                    method: 'POST',
                    url: `/client-file/upload_to_aws/`,
                    data,
                    noCms: true
                }
            },
        }),
        createIcon: builder.mutation<
            {
                id: number,
                name: string,
                file: AwsFile
            },
            {
                fileId: number,
                name: string
            }
        >({
            query: (options) => {
                return {
                    method: 'POST',
                    url: `/file/`,
                    data: {
                        name: options.name,
                        file: options.fileId
                    },
                }
            },
            invalidatesTags: ['icons']
        }),
        getIcons: builder.query<
            {
                results: {
                    id: number,
                    name: string,
                    file: AwsFile
                }[]
            },
            void
        >({
            query: () => {
                return {
                    url: `/file/`,
                }
            },
            providesTags: ['icons']
        }),
        downloadFile: builder.query<
            string,
            {
                bucket_name: string,
                file_name: string,
                object_name: string,
            }
        >({
            query: (options) => {
                return {
                    url: `/client-file/download_from_aws/`,
                    params: options,
                    noCms: true
                }
            },
        }),
        deleteDynamicPage: builder.mutation<
            undefined,
            number
        >({
            query: (pageId) => {
                return {
                    method: 'DELETE',
                    url: `/dynamic-page/${pageId}/`
                }
            },
            invalidatesTags: (page, params, pageId) => [
                'dynamic-pages',
                { type: 'dynamic-page', id: pageId }
            ]
        }),
        deleteMenu: builder.mutation<
            undefined,
            number
        >({
            query: (menuId) => {
                return {
                    method: 'DELETE',
                    url: `/menu/${menuId}/`
                }
            },
            invalidatesTags: (page, params, menuId) => [
                'menus',
                { type: 'menu', id: menuId }
            ]
        }),
        deleteSchemaTemplate: builder.mutation<
            undefined,
            number
        >({
            query: (schemaTemplateId) => {
                return {
                    method: 'DELETE',
                    url: `/schema-template/${schemaTemplateId}/`
                }
            },
            invalidatesTags: ['schema-templates']
        }),
        editMenu: builder.mutation<
            PageBuilderMenu,
            Partial<Omit<PageBuilderMenu, 'id' | 'creator' | 'last_author'>> & {
                id: number | string
            }
        >({
            query: (menu) => {
                const { id, ...data } = menu;

                function findPages(items: MenuContents): DynamicPage[] {
                    let results: DynamicPage[] = [];

                    for (const item of items) {
                        if (item.type === 'page' && item.page) {
                            results.push(item.page);
                        } else if (item.type === 'parent') {
                            results = results.concat(findPages(item.children));
                        }
                    }

                    return results;
                }

                const pages = findPages(menu.data ?? []);

                function normalizeData(contents: MenuContents): TMenuContents<number> {
                    const results: TMenuContents<number> = [];

                    for (let item of contents) {
                        let resultItem = {
                            ...item,
                        } as TMenuContents<number>[number];

                        if (resultItem.type !== 'parent' && 'children' in resultItem) {
                            const { children, ...rest } = resultItem;
                            resultItem = rest;
                        }

                        if (resultItem.type !== 'action' && 'action' in resultItem) {
                            const { action, ...rest } = resultItem;
                            resultItem = rest;
                        }

                        if (resultItem.type !== 'data-source' && 'source' in resultItem) {
                            const { source, ...rest } = resultItem;
                            resultItem = rest;
                        }

                        if (resultItem.type !== 'page' && 'page' in resultItem) {
                            const { page, ...rest } = resultItem;
                            resultItem = rest;
                        }

                        if (resultItem.type !== 'custom' && 'url' in resultItem) {
                            const { url, ...rest } = resultItem;
                            resultItem = rest;
                        }

                        if (item.type === 'parent' && resultItem.type === 'parent') {
                            resultItem.children = normalizeData(item.children);
                        }

                        if (item.type === 'page' && resultItem.type === 'page') {
                            resultItem.page = item.page?.id ?? null;
                        }

                        results.push(resultItem);
                    }

                    return results;
                }

                return {
                    method: 'PATCH',
                    url: `/menu/${id}/`,
                    data: {
                        ...data,
                        data: data.data ?
                            normalizeData(data.data) :
                            undefined,
                        pages: pages.map((page) => {
                            return page.id;
                        })
                    }
                }
            },
            invalidatesTags: () => [
                'menus',
                'menu'
            ]
        }),
        editSchemaTemplate: builder.mutation<
            SchemaTemplate,
            Partial<Omit<SchemaTemplate, 'id'>> & {
                id: number
            }
        >({
            query: (schemaTemplate) => {
                const { id, ...data } = schemaTemplate;
                return {
                    method: 'PATCH',
                    url: `/schema-template/${id}/`,
                    data
                }
            },
            invalidatesTags: ['schema-templates']
        }),
        getHomePageDestinations: builder.query<
            {
                results: Destination[]
            },
            {
                funnelId?: number
            }
        >({
            query: (options) => {
                return {
                    url: '/destinations/',
                    headers: {
                        'X-Cms-Funnel': options.funnelId
                    },
                    params: {
                        limit: 1000,
                        visibility__in: 'PUBLIC,PRIVATE_TO',
                        ordering: '-current_version__note',
                        homepage: true,
                    },
                    noCms: true
                };
            },
            providesTags: ['home-page-destinations']
        }),
        getDestinations: builder.query<
            {
                results: Destination[]
            },
            void
        >({
            query: () => {
                return {
                    url: '/destinations/',
                    params: {
                        limit: 1000,
                        visibility__in: 'PUBLIC,PRIVATE_TO',
                        ordering: '-current_version__note',
                        homepage: true,
                    },
                    noCms: true
                };
            },
            providesTags: ['destinations']
        }),
        getDynamicPages: builder.query<
            {
                count: number,
                previous: null,
                next: null,
                results: DynamicPage[]
            },
            {
                isSystem?: boolean
            }
        >({
            query: (options) => {
                return {
                    url: '/dynamic-page/',
                    params: {
                        is_system_page: options.isSystem
                    }
                }
            },
            transformResponse(
                data: {
                    count: number,
                    previous: null,
                    next: null,
                    results: DynamicPage[]
                },
                _,
                params
            ) {
                const pages = data.results;

                return {
                    ...data,
                    results: !params.isSystem ?
                        pages.filter((page) => {
                            return !['header', 'footer'].includes(page.name);
                        }) :
                        pages
                }
            },
            providesTags: ['dynamic-pages']
        }),
        getDynamicPage: builder.query<
            DynamicPage | undefined,
            {
                pageId?: number,
                name?: string,
                url?: string
            }
        >({
            query: (options) => {
                if (options.pageId) {
                    return { url: `/dynamic-page/${options.pageId}/` }
                }
                return {
                    url: `/dynamic-page/`,
                    params: {
                        name: options.name,
                        url: options.url
                    }
                }
            },
            transformResponse: (result: { results: DynamicPage[] } | DynamicPage) => {
                let page: DynamicPage | undefined;

                if (!('results' in result)) {
                    page = result;
                } else {
                    page = result.results[0];
                }

                function normalize(props: any) {
                    if (
                        isObject(props) &&
                        (
                            'xs' in props ||
                            'md' in props ||
                            'lg' in props
                        )
                    ) {
                        const _props = props as Styles[keyof Styles];
                        const max = [
                            _props?.lg,
                            _props?.md,
                            _props?.xs
                        ].filter((item) => {
                            return item !== undefined;
                        })[0];

                        if (max && _props) {
                            if (!('xs' in _props)) {
                                _props.xs = max;
                            }
                            if (!('md' in _props)) {
                                _props.md = max;
                            }
                            if (!('lg' in _props)) {
                                _props.lg = max;
                            }
                        }
                        return;
                    }

                    if (isObject(props)) {
                        for (const key in props) {
                            if (key !== 'display') {
                                normalize((props as any)[key]);
                            }
                        }
                    }
                }

                if (page) {
                    page.data = page?.data ?
                        JSON.stringify(
                            produce(
                                JSON.parse(page.data),
                                normalize
                            )
                        ) :
                        null;
                    page.draft_data = page?.draft_data ?
                        JSON.stringify(
                            produce(
                                JSON.parse(page.draft_data),
                                normalize
                            )
                        ) :
                        null;
                }

                return page;
            },
            providesTags: (page) => [{ type: 'dynamic-page', id: page?.id }]
        }),
        getFunnelComposition: builder.query<
            {
                results: FunnelComposition[]
            },
            void
        >({
            query: () => {
                return {
                    url: `/funnel-composition/`
                }
            },
            providesTags: ['funnel-composition']
        }),
        getTripCategories: builder.query<
            {
                count: number,
                previous: null,
                next: null,
                results: TripType[]
            },
            void
        >({
            query: () => {
                return {
                    url: '/circuit-category/',
                    params: {
                        limit: 150
                    },
                    noCms: true
                }
            },
            providesTags: ['trip-types']
        }),
        editFunnelComposition: builder.mutation<
            FunnelComposition[],
            Partial<FunnelComposition>[]
        >({
            query: (data) => {
                return {
                    method: 'PATCH',
                    url: '/funnel-composition/array_patch/',
                    data
                }
            },
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const response = await queryFulfilled;
                    dispatch(
                        pageBuilderApi.util.updateQueryData(
                            'getFunnelComposition',
                            undefined,
                            (draft) => {
                                draft.results = response.data;
                            }
                        )
                    );
                } catch {
                    dispatch(
                        pageBuilderApi.util.invalidateTags(
                            ['funnel-composition']
                        )
                    );
                }
            }
        }),
        getMenus: builder.query<
            {
                results: PageBuilderMenu[]
            },
            void
        >({
            query: () => {
                return {
                    url: `/menu/`
                }
            },
            providesTags: ['menus']
        }),
        getMenu: builder.query<
            PageBuilderMenu,
            number | string
        >({
            query: (menuId) => {
                return {
                    url: `/menu/${menuId}/`
                }
            },
            transformResponse(menu: TPageBuilderMenu<TMenuContents<number>>) {
                function normalizeData(contents: TMenuContents<number>): MenuContents {
                    const results: MenuContents = [];

                    for (let item of contents) {
                        let resultItem = {
                            ...item,
                        } as MenuContents[number];

                        if (item.type === 'parent' && resultItem.type === 'parent') {
                            resultItem.children = normalizeData(item.children);
                        }

                        if (item.type === 'page' && resultItem.type === 'page') {
                            resultItem.page = menu.pages.find((page) => {
                                return page.id === item.page;
                            }) ?? null;
                        }

                        results.push(resultItem);
                    }

                    return results;
                }

                return {
                    ...menu,
                    data: normalizeData(menu.data)
                };
            },
            providesTags: (result, params, menuId) => [{ type: 'menu', id: menuId }]
        }),
        getSchemaTemplates: builder.query<
            {
                results: SchemaTemplate[]
            },
            void
        >({
            query: () => {
                return {
                    url: '/schema-template/'
                }
            },
            providesTags: ['schema-templates']
        }),
        updateDynamicPage: builder.mutation<
            DynamicPage,
            Partial<Omit<DynamicPage, 'id' | 'parent'> & { parent: number | null }> & {
                id: number
            }
        >({
            query: (page) => {
                const { id, ...rest } = page;
                return {
                    method: 'PATCH',
                    url: `/dynamic-page/${id}/`,
                    data: {
                        ...rest,
                        pages: undefined
                    }
                }
            },
            invalidatesTags: (result, params, page) => [
                'dynamic-pages',
                { type: 'dynamic-page', id: page.id }
            ]
        }),
        updateUser: builder.mutation<
            User,
            Partial<Omit<User, 'id' | 'using_currency'>> & {
                id: number,
                using_currency?: number
            }
        >({
            query: (user) => {
                const { id, ...rest } = user;
                return {
                    method: 'PATCH',
                    url: `/user/${id}/`,
                    data: rest,
                    noCms: true
                }
            },
            invalidatesTags: (result, params, user) => [
                { type: 'user', id: user.id }
            ]
        })
    })
});

export const {
    useCreateDynamicPageMutation,
    useCreateSchemaTemplateMutation,
    useCreateMenuMutation,
    useDeleteDynamicPageMutation,
    useDeleteMenuMutation,
    useDeleteSchemaTemplateMutation,
    useEditMenuMutation,
    useEditSchemaTemplateMutation,
    useGetDynamicPagesQuery,
    useGetDynamicPageQuery,
    useGetMenusQuery,
    useGetMenuQuery,
    useLazyGetMenuQuery,
    useGetSchemaTemplatesQuery,
    useGetFunnelCompositionQuery,
    useGetHomePageDestinationsQuery,
    useGetDestinationsQuery,
    useGetTripCategoriesQuery,
    useUpdateDynamicPageMutation,
    useEditFunnelCompositionMutation,
    useUploadFileMutation,
    useCreateIconMutation,
    useGetIconsQuery,
    useDownloadFileQuery,
    useUpdateUserMutation
} = pageBuilderApi;